import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useAdminExtras } from "@/hooks/useAdminExtras";
import { getMyRole } from "@/lib/admin.functions";
import {
  EXPERT_VISIBILITY_DEFAULT,
  getExpertVisibility,
  type ExpertVisibility,
} from "@/lib/expert-visibility.functions";

export type ExpertFeatures = {
  /** Får expertytorna visas för den här besökaren? */
  enabled: boolean;
  /** Visas bara för administratörer just nu – då märks ytorna med "Test". */
  testOnly: boolean;
  mode: ExpertVisibility;
};

/**
 * Superadmin styr expertläget: av, bara för administratörer (testmärkt) eller
 * publikt. Administratörer ser dessutom sajten som en vanlig användare tills
 * de själva slår på extrafunktionerna – och aldrig på liveadressen.
 */
export function useExpertFeatures(): ExpertFeatures {
  const { user } = useAuth();
  const { extras } = useAdminExtras();
  const { data: setting } = useQuery({
    queryKey: ["expert-visibility"],
    queryFn: () => getExpertVisibility(),
    staleTime: 5 * 60 * 1000,
  });
  const { data: roleData } = useQuery({
    queryKey: ["my-role", user?.id],
    queryFn: () => getMyRole(),
    enabled: !!user,
  });

  const mode = setting?.mode ?? EXPERT_VISIBILITY_DEFAULT;
  const isAdmin = !!roleData?.isAdmin && extras;
  const enabled = mode === "public" || (mode === "admins" && isAdmin);
  return { enabled, testOnly: enabled && mode !== "public", mode };
}
